/**
 * Centralized Logging Service
 * Provides structured logging with file output for debugging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogEntry {
  timestamp: string;
  level: string;
  category: string;
  message: string;
  data?: unknown;
  stack?: string;
}

export interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableFile: boolean;
  fileLogDir: string;
}

/**
 * Logger class for structured logging
 */
class Logger {
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      minLevel: LogLevel.INFO,
      enableConsole: true,
      enableFile: true,
      fileLogDir: '/logs',
      ...config,
    };

    // Flush logs every 5 seconds. In Node the handle is a Timeout: unref it, or
    // any process that imports the logger (vite build via api/_lib) never exits.
    const timer = setInterval(() => this.flush(), 5000);
    if (typeof timer === 'object' && timer !== null && 'unref' in timer && typeof timer.unref === 'function') timer.unref();
    this.flushTimer = timer;
  }

  /**
   * Format timestamp
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Get level name
   */
  private getLevelName(level: LogLevel): string {
    return LogLevel[level];
  }

  /**
   * Create log entry
   */
  private createEntry(
    level: LogLevel,
    category: string,
    message: string,
    data?: unknown,
    error?: Error
  ): LogEntry {
    return {
      timestamp: this.getTimestamp(),
      level: this.getLevelName(level),
      category,
      message,
      data,
      stack: error?.stack,
    };
  }

  /**
   * Check if level should be logged
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.minLevel;
  }

  /**
   * Log to console
   */
  private logToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const { timestamp, level, category, message, data, stack } = entry;
    const prefix = `[${timestamp}] [${level}] [${category}]`;

    switch (entry.level) {
      case 'ERROR':
        console.error(prefix, message, data || '');
        if (stack) console.error(stack);
        break;
      case 'WARN':
        console.warn(prefix, message, data || '');
        break;
      case 'INFO':
        // eslint-disable-next-line no-console
        console.info(prefix, message, data || '');
        break;
      case 'DEBUG':
        // eslint-disable-next-line no-console
        console.debug(prefix, message, data || '');
        break;
    }
  }

  /**
   * Add entry to buffer
   */
  private buffer(entry: LogEntry): void {
    this.logBuffer.push(entry);

    // Flush immediately for errors
    if (entry.level === 'ERROR') {
      this.flush();
    }
  }

  /**
   * Flush buffer to "file" (localStorage for browser)
   */
  private flush(): void {
    if (!this.config.enableFile || this.logBuffer.length === 0) return;
    // Server-side (Node, no localStorage): drop the buffer instead of persisting.
    if (typeof localStorage === 'undefined') { this.logBuffer = []; return; }

    try {
      // Store in localStorage with rotation
      const today = new Date().toISOString().split('T')[0];
      const storageKey = `logs_${today}`;
      const existing = localStorage.getItem(storageKey) || '[]';
      const logs = JSON.parse(existing) as LogEntry[];

      // Keep only last 1000 entries per day
      const combined = [...logs, ...this.logBuffer].slice(-1000);
      localStorage.setItem(storageKey, JSON.stringify(combined));

      // Keep last 7 days
      this.cleanupOldLogs();

      this.logBuffer = [];
    } catch (error) {
      console.error('[Logger] Failed to flush logs:', error);
    }
  }

  /**
   * Clean up old logs (keep last 7 days)
   */
  private cleanupOldLogs(): void {
    if (typeof localStorage === 'undefined') return;
    const keys = Object.keys(localStorage);
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    keys.forEach((key) => {
      if (key.startsWith('logs_')) {
        const dateStr = key.replace('logs_', '');
        const logDate = new Date(dateStr);
        if (logDate < sevenDaysAgo) {
          localStorage.removeItem(key);
        }
      }
    });
  }

  /**
   * Debug level log
   */
  debug(category: string, message: string, data?: unknown): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    const entry = this.createEntry(LogLevel.DEBUG, category, message, data);
    this.logToConsole(entry);
    this.buffer(entry);
  }

  /**
   * Info level log
   */
  info(category: string, message: string, data?: unknown): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    const entry = this.createEntry(LogLevel.INFO, category, message, data);
    this.logToConsole(entry);
    this.buffer(entry);
  }

  /**
   * Warning level log
   */
  warn(category: string, message: string, data?: unknown): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    const entry = this.createEntry(LogLevel.WARN, category, message, data);
    this.logToConsole(entry);
    this.buffer(entry);
  }

  /**
   * Error level log
   */
  error(category: string, message: string, error?: Error | unknown, data?: unknown): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    const entry = this.createEntry(
      LogLevel.ERROR,
      category,
      message,
      data,
      error instanceof Error ? error : undefined
    );
    this.logToConsole(entry);
    this.buffer(entry);
  }

  /**
   * Get all logs from storage
   */
  getLogs(days = 1): LogEntry[] {
    if (typeof localStorage === 'undefined') return [];
    const logs: LogEntry[] = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const storageKey = `logs_${dateStr}`;
      const data = localStorage.getItem(storageKey);

      if (data) {
        try {
          const dayLogs = JSON.parse(data) as LogEntry[];
          logs.push(...dayLogs);
        } catch {
          // Ignore parse errors
        }
      }
    }

    return logs.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }

  /**
   * Get logs as formatted text
   */
  getLogsAsText(days = 1): string {
    const logs = this.getLogs(days);
    return logs
      .map(
        (entry) =>
          `[${entry.timestamp}] [${entry.level}] [${entry.category}] ${entry.message}${
            entry.data ? '\n  ' + JSON.stringify(entry.data, null, 2) : ''
          }${entry.stack ? '\n  ' + entry.stack : ''}`
      )
      .join('\n');
  }

  /**
   * Export logs as JSON
   */
  exportLogs(days = 1): string {
    return JSON.stringify(this.getLogs(days), null, 2);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    if (typeof localStorage === 'undefined') return;
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('logs_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get log statistics
   */
  getStats(): Record<string, number> {
    const logs = this.getLogs(7);
    const stats: Record<string, number> = {
      total: logs.length,
      ERROR: 0,
      WARN: 0,
      INFO: 0,
      DEBUG: 0,
    };

    logs.forEach((log) => {
      stats[log.level] = (stats[log.level] || 0) + 1;
    });

    return stats;
  }

  /**
   * Destroy logger
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Global logger instance
const logger = new Logger();

// Export convenience functions
export const debug = (category: string, message: string, data?: unknown) =>
  logger.debug(category, message, data);

export const info = (category: string, message: string, data?: unknown) =>
  logger.info(category, message, data);

export const warn = (category: string, message: string, data?: unknown) =>
  logger.warn(category, message, data);

export const logError = (category: string, message: string, error?: Error | unknown, data?: unknown) =>
  logger.error(category, message, error, data);

export const getLogs = (days?: number) => logger.getLogs(days);
export const getLogsAsText = (days?: number) => logger.getLogsAsText(days);
export const exportLogs = (days?: number) => logger.exportLogs(days);
export const clearLogs = () => logger.clearLogs();
export const getStats = () => logger.getStats();

export default logger;
